import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
  alpha,
} from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShareIcon from "@mui/icons-material/Share";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import BuildIcon from "@mui/icons-material/Build";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useReactToPrint } from "react-to-print";
import AssetService from "../../../service/asset";
import DepartmentServices from "../../../service/department";
import DashboardServices from "../../../service/dashboard";

/* -------------------------------------------------------------------------- */
/*  Design tokens (shared with Overview)                                      */
/* -------------------------------------------------------------------------- */
const COLORS = {
  primary: "#0F4C75",
  primaryLight: "#3282B8",
  accent: "#1B9C85",
  success: "#2E7D32",
  warning: "#ED6C02",
  error: "#D32F2F",
  surface: "#FFFFFF",
  background: "#F4F7FA",
  border: "#E2E8F0",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  muted: "#94A3B8",
};

const CHART_COLORS = {
  A: "#D32F2F",
  B: "#ED6C02",
  C: "#1B9C85",
  line: "#0F4C75",
};

const PERIODS = [
  { label: "Today", value: "TODAY" },
  { label: "Yesterday", value: "YESTERDAY" },
  { label: "This Week", value: "WEEK" },
  { label: "This Month", value: "MONTH" },
  { label: "This Year", value: "YEAR" },
  { label: "Last 30 days", value: "DAYS", days: 30 },
  { label: "Custom", value: "CUSTOM" },
];

/* -------------------------------------------------------------------------- */
/*  Date utilities                                                            */
/* -------------------------------------------------------------------------- */
const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());
const endOfDay = (date) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  );
const isoDate = (date) => date.toISOString().slice(0, 10);

const getDateRange = (period, customStart, customEnd) => {
  const today = startOfDay(new Date());
  let start = today;
  let end = endOfDay(today);

  switch (period) {
    case "TODAY":
      break;
    case "YESTERDAY":
      start = new Date(today);
      start.setDate(start.getDate() - 1);
      end = endOfDay(start);
      break;
    case "WEEK":
      start = new Date(today);
      start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
      break;
    case "MONTH":
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case "YEAR":
      start = new Date(today.getFullYear(), 0, 1);
      break;
    case "DAYS":
      start = new Date(today);
      start.setDate(start.getDate() - 30);
      break;
    case "CUSTOM":
      if (customStart) {
        start = startOfDay(new Date(`${customStart}T00:00:00`));
      }
      if (customEnd) {
        end = endOfDay(new Date(`${customEnd}T00:00:00`));
      } else if (customStart) {
        end = endOfDay(start);
      }
      break;
    default:
      break;
  }

  return { start, end };
};

/* -------------------------------------------------------------------------- */
/*  Shared UI components                                                      */
/* -------------------------------------------------------------------------- */
const MetricCard = ({
  label,
  value,
  hint,
  icon: Icon,
  color = COLORS.primary,
}) => (
  <Card
    elevation={0}
    sx={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: 2.5,
      height: "100%",
      backgroundColor: COLORS.surface,
      transition: "box-shadow 0.2s ease",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
      },
    }}
  >
    <CardContent
      sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.textSecondary,
              fontWeight: 500,
              mb: 0.75,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              color,
              fontWeight: 700,
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
              fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
            }}
          >
            {value}
          </Typography>
          {hint && (
            <Typography
              variant="caption"
              sx={{ color: COLORS.muted, mt: 0.75, display: "block" }}
            >
              {hint}
            </Typography>
          )}
        </Box>
        {Icon && (
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              backgroundColor: alpha(color, 0.1),
              color,
              flexShrink: 0,
              ml: 1,
            }}
          >
            <Icon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </Box>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const SectionCard = ({ title, description, children, action, sx }) => (
  <Card
    elevation={0}
    sx={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: 2.5,
      mb: 2,
      backgroundColor: COLORS.surface,
      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
      ...sx,
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 2.5, md: 2.75 },
        "&:last-child": { pb: { xs: 2, sm: 2.5, md: 2.75 } },
      }}
    >
      {(title || action) && (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={2}
          spacing={1}
        >
          <Box sx={{ minWidth: 0 }}>
            {title && (
              <Typography
                variant="subtitle1"
                fontWeight={650}
                sx={{ color: COLORS.textPrimary, letterSpacing: "-0.01em" }}
              >
                {title}
              </Typography>
            )}
            {description && (
              <Typography
                variant="caption"
                sx={{ color: COLORS.textSecondary, display: "block", mt: 0.25 }}
              >
                {description}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
      )}
      {children}
    </CardContent>
  </Card>
);

const EmptyState = ({ message }) => (
  <Box
    sx={{
      minHeight: { xs: 140, sm: 180 },
      display: "grid",
      placeItems: "center",
      textAlign: "center",
      color: COLORS.textSecondary,
      border: `1px dashed ${COLORS.border}`,
      borderRadius: 2,
      p: { xs: 2, sm: 3 },
      backgroundColor: alpha(COLORS.background, 0.6),
    }}
  >
    <Typography variant="body2" sx={{ maxWidth: 280 }}>
      {message}
    </Typography>
  </Box>
);

const SectionHeading = ({ title }) => (
  <Typography
    variant="h6"
    fontWeight={700}
    sx={{
      color: COLORS.textPrimary,
      letterSpacing: "-0.02em",
      mb: 2,
      fontSize: { xs: "1.05rem", sm: "1.15rem" },
    }}
  >
    {title}
  </Typography>
);

const StatusChip = ({ status }) => {
  const map = {
    Due: { color: "error", bg: alpha(COLORS.error, 0.1) },
    Approaching: { color: "warning", bg: alpha(COLORS.warning, 0.12) },
    Healthy: { color: "success", bg: alpha(COLORS.success, 0.1) },
  };
  const config = map[status] || { color: "default", bg: "transparent" };

  return (
    <Chip
      size="small"
      label={status}
      color={config.color}
      variant={status === "Healthy" ? "outlined" : "filled"}
      sx={{
        fontWeight: 600,
        fontSize: "0.75rem",
        height: 24,
        backgroundColor: status !== "Healthy" ? config.bg : undefined,
      }}
    />
  );
};

/* -------------------------------------------------------------------------- */
/*  Report Header                                                             */
/* -------------------------------------------------------------------------- */
const ReportHeader = ({
  reportType,
  period,
  periodLabel,
  onShare,
  onExportPdf,
}) => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    justifyContent="space-between"
    alignItems={{ xs: "stretch", md: "flex-start" }}
    spacing={2}
    sx={{
      mb: 3,
      pb: 2.5,
      borderBottom: `1px solid ${COLORS.border}`,
      borderLeft: { xs: "none", md: `4px solid ${COLORS.primary}` },
      pl: { xs: 0, md: 2.25 },
    }}
  >
    <Box>
      <Typography
        variant="h6"
        fontWeight={650}
        sx={{
          color: COLORS.textPrimary,
          letterSpacing: "-0.03em",
          lineHeight: 1.15,
          fontSize: { xs: "1.2rem", sm: "1.3rem", md: "1.5rem" },
          mb: 0.56
        }}
      >
        {reportType === "GENERAL" && "Reports"}
        {reportType === "PRESTART" && "Pre-start Compliance"}
        {reportType === "ASSET" && "Asset Compliance"}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: COLORS.textSecondary, mb: 1.25 }}
      >
        Monitor fleet inspection, pre-start, asset and service compliance.
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          label={`Period: ${periodLabel}`}
          size="small"
          variant="outlined"
          sx={{
            borderColor: COLORS.border,
            fontWeight: 500,
            height: 26,
          }}
        />
        <Chip
          label={`${isoDate(period.start)} → ${isoDate(period.end)}`}
          size="small"
          variant="outlined"
          sx={{
            borderColor: COLORS.border,
            fontWeight: 500,
            height: 26,
          }}
        />
      </Stack>
    </Box>

    <Stack
      className="no-print"
      direction={{ xs: "column", sm: "row" }}
      spacing={1.25}
      alignItems="stretch"
      sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}
    >
      <Button
        variant="outlined"
        startIcon={<ShareIcon />}
        onClick={onShare}
        fullWidth={{ xs: true, sm: false }}
        sx={{
          borderRadius: 1.5,
          borderColor: COLORS.border,
          color: COLORS.textPrimary,
          fontWeight: 600,
          backgroundColor: COLORS.surface,
          "&:hover": {
            borderColor: COLORS.primaryLight,
            backgroundColor: alpha(COLORS.primary, 0.04),
          },
        }}
      >
        Share
      </Button>
      <Button
        variant="contained"
        startIcon={<PictureAsPdfIcon />}
        onClick={onExportPdf}
        fullWidth={{ xs: true, sm: false }}
        sx={{
          borderRadius: 1.5,
          fontWeight: 600,
          backgroundColor: COLORS.primary,
          boxShadow: "none",
          "&:hover": {
            backgroundColor: COLORS.primaryLight,
            boxShadow: "none",
          },
        }}
      >
        PDF
      </Button>
    </Stack>
  </Stack>
);

/* -------------------------------------------------------------------------- */
/*  General Report                                                            */
/* -------------------------------------------------------------------------- */
const GeneralReport = ({ report, isLoading, isFetching }) => {
  const {
    totalAssets = 0,
    totalInspections = 0,
    completed = 0,
    completionRate = 0,
    affectedAssets = 0,
    periodIssues = 0,
    categoryA = 0,
    serviceDue = 0,
    attentionAssets = [],
    departmentBreakdown = [],
    overallTargetMet = 0,
    assetDetails = [],
  } = report || {};

  if (isLoading) {
    return (
      <Box>
        <Grid container spacing={2} mb={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton
                variant="rounded"
                height={110}
                sx={{ borderRadius: 2.5 }}
              />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2.5 }} />
      </Box>
    );
  }

  return (
    <Box>
      {isFetching && (
        <LinearProgress
          sx={{
            mb: 2.5,
            borderRadius: 1,
            height: 3,
            backgroundColor: alpha(COLORS.primary, 0.08),
            "& .MuiLinearProgress-bar": {
              backgroundColor: COLORS.primaryLight,
            },
          }}
        />
      )}

      <SectionHeading title="Executive Summary" />
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Total assets"
            value={totalAssets}
            icon={DirectionsCarIcon}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Total inspections"
            value={totalInspections}
            icon={AssignmentTurnedInIcon}
            color={COLORS.primaryLight}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Pre-start completion"
            value={`${completionRate}%`}
            hint={`${completed} of ${totalInspections}`}
            color={completionRate >= 80 ? COLORS.success : COLORS.warning}
            icon={CheckCircleOutlineIcon}
          />
        </Grid>
     
        {overallTargetMet !== undefined && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="Overall Target Met %"
              value={`${overallTargetMet}%`}
              color={
                overallTargetMet >= 80
                  ? COLORS.success
                  : COLORS.warning
              }
              icon={CheckCircleOutlineIcon}
            />
          </Grid>
        )}  
      </Grid>

      {(categoryA > 0 || serviceDue > 0) && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{
            mb: 2.5,
            borderRadius: 2,
            border: `1px solid ${alpha(COLORS.warning, 0.3)}`,
            alignItems: "flex-start",
          }}
        >
          <Stack spacing={0.75}>
            {categoryA > 0 && (
              <Typography variant="body2">
                <strong>{categoryA}</strong> Category A (No-Go) issue
                {categoryA === 1 ? "" : "s"} require immediate attention.
              </Typography>
            )}
            {serviceDue > 0 && (
              <Typography variant="body2">
                <strong>{serviceDue}</strong> asset{serviceDue === 1 ? "" : "s"}{" "}
                have reached the service interval.
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      {/* Compliance Analytics Section */}
      {(departmentBreakdown.length > 0 || assetDetails.length > 0) && (
        <>
          <SectionHeading title="Compliance Analytics" />

          {/* Department-wise Compliance Chart */}
          {departmentBreakdown && departmentBreakdown.length > 0 && (
            <SectionCard title="Department-wise Target Met Percentage">
              <Box sx={{ width: "100%", height: { xs: 300, sm: 350 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={departmentBreakdown}
                    margin={{ left: 8, right: 8, top: 20, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke={COLORS.border}
                    />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis
                      domain={[0, 100]}
                      allowDecimals={false}
                      tick={{ fontSize: 12 }}
                      width={32}
                      label={{
                        value: "Target Met %",
                        angle: -90,
                        position: "insideLeft",
                        offset: 10,
                        style: { fontSize: 12, fill: COLORS.textSecondary },
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: `1px solid ${COLORS.border}`,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      }}
                      formatter={(value) => `${value}%`}
                    />
                    <Bar
                      dataKey="total"
                      fill={COLORS.primary}
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </SectionCard>
          )}

          

          {/* Asset Compliance Table */}
          {assetDetails && assetDetails.length > 0 && (
            <SectionCard
              title="Asset Compliance Details"
              description="Target compliance, current prestart (SMR), and service cycle information"
            >
              <TableContainer
                sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}
              >
                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 650,
                          color: COLORS.textSecondary,
                          borderBottom: `1px solid ${COLORS.border}`,
                        }}
                      >
                        Asset ID
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 650,
                          color: COLORS.textSecondary,
                          borderBottom: `1px solid ${COLORS.border}`,
                        }}
                      >
                        Target
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 650,
                          color: COLORS.textSecondary,
                          borderBottom: `1px solid ${COLORS.border}`,
                        }}
                      >
                        Current (SMR)
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 650,
                          color: COLORS.textSecondary,
                          borderBottom: `1px solid ${COLORS.border}`,
                        }}
                      >
                        Target Met %
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 650,
                          color: COLORS.textSecondary,
                          borderBottom: `1px solid ${COLORS.border}`,
                        }}
                      >
                        Service Cycle
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 650,
                          color: COLORS.textSecondary,
                          borderBottom: `1px solid ${COLORS.border}`,
                        }}
                      >
                        Service Interval
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assetDetails.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ py: 1.35 }}>
                          {row.assetId}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {row.target}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {row.prestart_count}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {row.target_met_percentage}%
                        </TableCell>
                        <TableCell sx={{ py: 1.35 }}>
                          {row.serviceCycle || "N/A"}
                        </TableCell>
                        <TableCell sx={{ py: 1.35 }}>
                          {row.serviceInterval || "N/A"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </SectionCard>
          )}
        </>
      )}

      <SectionCard
        title="Assets requiring attention"
        description="Assets with issues or approaching service interval"
      >
        {attentionAssets.length > 0 ? (
          <TableContainer
            sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}
          >
            <Table size="small" sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow>
                  {[
                    "Asset",
                    "Issues",
                    "Category A",
                    "Category B",
                    "Category C",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 650,
                        color: COLORS.textSecondary,
                        borderBottom: `1px solid ${COLORS.border}`,
                        py: 1.25,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {attentionAssets.map((row) => (
                  <TableRow key={row.assetId} hover>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                      {row.assetId}
                    </TableCell>
                    <TableCell>{row.issues}</TableCell>
                    <TableCell>
                      {row.categoryA > 0 && (
                        <Chip
                          label={row.categoryA}
                          size="small"
                          sx={{
                            backgroundColor: CHART_COLORS.A,
                            color: "white",
                            fontWeight: 600,
                            height: 22,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.categoryB > 0 && (
                        <Chip
                          label={row.categoryB}
                          size="small"
                          sx={{
                            backgroundColor: CHART_COLORS.B,
                            color: "white",
                            fontWeight: 600,
                            height: 22,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.categoryC > 0 && (
                        <Chip
                          label={row.categoryC}
                          size="small"
                          sx={{
                            backgroundColor: CHART_COLORS.C,
                            color: "white",
                            fontWeight: 600,
                            height: 22,
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyState message="No assets require attention during this period." />
        )}
      </SectionCard>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Pre-start Report                                                          */
/* -------------------------------------------------------------------------- */
const PrestartReport = ({ report, isLoading, isFetching }) => {
  const {
    totalPrestarts = 0,
    completed = 0,
    completionRate = 0,
    categoryTotals = { A: 0, B: 0, C: 0 },
    withIssues = 0,
    withoutIssues = 0,
    issuePercentage = 0,
    trend = [],
    assetBreakdown = [],
  } = report || {};

  const issueSummary = [
    { label: "Pre-starts with issues", value: withIssues },
    { label: "Pre-starts without issues", value: withoutIssues },
    { label: "Issue rate", value: `${issuePercentage}%` },
  ];

  if (isLoading) {
    return (
      <Box>
        <Grid container spacing={2} mb={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton
                variant="rounded"
                height={110}
                sx={{ borderRadius: 2.5 }}
              />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2.5 }} />
      </Box>
    );
  }

  return (
    <Box>
      {isFetching && (
        <LinearProgress
          sx={{
            mb: 2.5,
            borderRadius: 1,
            height: 3,
            backgroundColor: alpha(COLORS.primary, 0.08),
            "& .MuiLinearProgress-bar": {
              backgroundColor: COLORS.primaryLight,
            },
          }}
        />
      )}

      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Total pre-starts"
            value={totalPrestarts}
            icon={AssignmentTurnedInIcon}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Completed"
            value={completed}
            icon={CheckCircleOutlineIcon}
            color={COLORS.success}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Completion rate"
            value={`${completionRate}%`}
            color={completionRate >= 80 ? COLORS.success : COLORS.warning}
            icon={CheckCircleOutlineIcon}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Pre-starts with issues"
            value={withIssues}
            color={withIssues > 0 ? COLORS.error : COLORS.success}
            icon={ReportProblemIcon}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Inspection activity"
            description="Pre-start count over the selected period"
          >
            {trend.length > 0 ? (
              <Box sx={{ width: "100%", height: { xs: 220, sm: 260 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={trend}
                    margin={{ left: 0, right: 8, top: 8, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke={COLORS.border}
                    />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 12 }}
                      width={32}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: `1px solid ${COLORS.border}`,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke={CHART_COLORS.line}
                      strokeWidth={2.5}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <EmptyState message="No pre-start activity for the selected range." />
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="Inspection issues"
            description="Category A/B/C distribution"
          >
            {Object.values(categoryTotals).some(Boolean) ? (
              <Stack spacing={2}>
                {[
                  {
                    label: "Category A",
                    value: categoryTotals.A,
                    color: CHART_COLORS.A,
                  },
                  {
                    label: "Category B",
                    value: categoryTotals.B,
                    color: CHART_COLORS.B,
                  },
                  {
                    label: "Category C",
                    value: categoryTotals.C,
                    color: CHART_COLORS.C,
                  },
                ].map((item) => (
                  <Stack
                    key={item.label}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: item.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2">{item.label}</Typography>
                    </Stack>
                    <Typography variant="h6" fontWeight={700}>
                      {item.value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <EmptyState message="No pre-start issues recorded for this period." />
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <SectionCard
        title="Pre-start summary"
        description="Issue rate and inspection outcomes"
      >
        <Grid container spacing={2}>
          {issueSummary.map((item) => (
            <Grid item xs={12} sm={4} key={item.label}>
              <MetricCard label={item.label} value={item.value} />
            </Grid>
          ))}
        </Grid>
      </SectionCard>

      <SectionCard
        title="Pre-start by asset"
        description="Assets with inspection activity and issue counts"
      >
        {assetBreakdown.length > 0 ? (
          <TableContainer
            sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}
          >
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  {[
                    "Asset",
                    "Pre-starts",
                    "Issues",
                    "Cat A",
                    "Cat B",
                    "Cat C",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 650,
                        color: COLORS.textSecondary,
                        borderBottom: `1px solid ${COLORS.border}`,
                        py: 1.25,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {assetBreakdown.map((row) => (
                  <TableRow key={row.assetId} hover>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                      {row.assetId}
                    </TableCell>
                    <TableCell>{row.count}</TableCell>
                    <TableCell>{row.issues}</TableCell>
                    <TableCell>
                      {row.categoryA > 0 ? (
                        <Chip
                          label={row.categoryA}
                          size="small"
                          sx={{
                            backgroundColor: CHART_COLORS.A,
                            color: "white",
                            fontWeight: 600,
                            height: 22,
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {row.categoryB > 0 ? (
                        <Chip
                          label={row.categoryB}
                          size="small"
                          sx={{
                            backgroundColor: CHART_COLORS.B,
                            color: "white",
                            fontWeight: 600,
                            height: 22,
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {row.categoryC > 0 ? (
                        <Chip
                          label={row.categoryC}
                          size="small"
                          sx={{
                            backgroundColor: CHART_COLORS.C,
                            color: "white",
                            fontWeight: 600,
                            height: 22,
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyState message="No pre-start records match the selected filters." />
        )}
      </SectionCard>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Asset Report                                                              */
/* -------------------------------------------------------------------------- */
const AssetReport = ({ report, isLoading, isFetching }) => {
  const {
    totalAssets = 0,
    inspectedCount = 0,
    coverage = 0,
    avgReading = 0,
    dueAssets = 0,
    approaching = 0,
    conditionSummary = [],
    departmentSummary = [],
    serviceRows = [],
  } = report || {};

  if (isLoading) {
    return (
      <Box>
        <Grid container spacing={2} mb={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton
                variant="rounded"
                height={110}
                sx={{ borderRadius: 2.5 }}
              />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2.5 }} />
      </Box>
    );
  }

  return (
    <Box>
      {isFetching && (
        <LinearProgress
          sx={{
            mb: 2.5,
            borderRadius: 1,
            height: 3,
            backgroundColor: alpha(COLORS.primary, 0.08),
            "& .MuiLinearProgress-bar": {
              backgroundColor: COLORS.primaryLight,
            },
          }}
        />
      )}

      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Total assets"
            value={totalAssets}
            icon={DirectionsCarIcon}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Assets inspected"
            value={inspectedCount}
            icon={AssignmentTurnedInIcon}
            color={COLORS.primaryLight}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Inspection coverage"
            value={`${coverage}%`}
            color={COLORS.primary}
            icon={CheckCircleOutlineIcon}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Average usage"
            value={avgReading.toLocaleString()}
            hint="Current reading value"
            icon={BuildIcon}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Service interval"
            description="Due and approaching assets"
          >
            <Stack spacing={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="body2"
                  sx={{ color: COLORS.textSecondary }}
                >
                  Approaching
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={COLORS.warning}
                >
                  {approaching}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="body2"
                  sx={{ color: COLORS.textSecondary }}
                >
                  Due
                </Typography>
                <Typography variant="h6" fontWeight={700} color={COLORS.error}>
                  {dueAssets}
                </Typography>
              </Stack>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard
            title="Condition"
            description="Current asset condition distribution"
          >
            {conditionSummary.length > 0 ? (
              <Stack spacing={1.5}>
                {conditionSummary.map(({ condition, count }) => (
                  <Stack
                    key={condition}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2">{condition}</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {count}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <EmptyState message="No condition data available for the current fleet." />
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <SectionCard
        title="Service priority table"
        description="Assets ranked by service urgency and operational risk"
      >
        {serviceRows.length > 0 ? (
          <TableContainer
            sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}
          >
            <Table size="small" sx={{ minWidth: 680 }}>
              <TableHead>
                <TableRow>
                  {[
                    "Asset",
                    "Department",
                    "Current",
                    "Next service",
                    "Remaining",
                    "Status",
                    "Condition",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 650,
                        color: COLORS.textSecondary,
                        borderBottom: `1px solid ${COLORS.border}`,
                        py: 1.25,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {serviceRows.map((row) => (
                  <TableRow key={row.assetId} hover>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                      {row.assetId}
                    </TableCell>
                    <TableCell sx={{ color: COLORS.textSecondary }}>
                      {row.department}
                    </TableCell>
                    <TableCell>
                      {Number.isFinite(row.current)
                        ? row.current.toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {Number.isFinite(row.interval)
                        ? row.interval.toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {Number.isFinite(row.remaining)
                        ? row.remaining.toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={row.status} />
                    </TableCell>
                    <TableCell>{row.condition}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyState message="No asset service data available for the selected range." />
        )}
      </SectionCard>

      <SectionCard
        title="Department distribution"
        description="Current fleet distribution by department"
      >
        {departmentSummary.length > 0 ? (
          <TableContainer
            sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}
          >
            <Table size="small" sx={{ minWidth: 320 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 650,
                      color: COLORS.textSecondary,
                      borderBottom: `1px solid ${COLORS.border}`,
                    }}
                  >
                    Department
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 650,
                      color: COLORS.textSecondary,
                      borderBottom: `1px solid ${COLORS.border}`,
                    }}
                  >
                    Asset count
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departmentSummary.map(({ department, count }) => (
                  <TableRow key={department} hover>
                    <TableCell sx={{ py: 1.35 }}>{department}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyState message="No department data available." />
        )}
      </SectionCard>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Main Compliance Component                                                 */
/* -------------------------------------------------------------------------- */
export const DashboardCompliance = () => {
  const reportRef = React.useRef(null);
  const [reportType, setReportType] = React.useState(
    () =>
      new URLSearchParams(window.location.search).get("report") || "GENERAL",
  );
  const [period, setPeriod] = React.useState(
    () => new URLSearchParams(window.location.search).get("period") || "MONTH",
  );
  const [customStart, setCustomStart] = React.useState(
    () => new URLSearchParams(window.location.search).get("start") || "",
  );
  const [customEnd, setCustomEnd] = React.useState(
    () => new URLSearchParams(window.location.search).get("end") || "",
  );
  const [prestartDepartment, setPrestartDepartment] = React.useState(
    () => new URLSearchParams(window.location.search).get("department") || "",
  );
  const [prestartSubDepartment, setPrestartSubDepartment] = React.useState(
    () =>
      new URLSearchParams(window.location.search).get("subDepartment") || "",
  );
  const [prestartCategory, setPrestartCategory] = React.useState(
    () => new URLSearchParams(window.location.search).get("category") || "",
  );
  const [assetDepartment, setAssetDepartment] = React.useState(
    () =>
      new URLSearchParams(window.location.search).get("assetDepartment") || "",
  );
  const [assetFilter, setAssetFilter] = React.useState(
    () => new URLSearchParams(window.location.search).get("asset") || "ALL",
  );
  const [shareMessage, setShareMessage] = React.useState("");

  // General tab compliance filter states
  const [generalDepartment, setGeneralDepartment] = React.useState("");
  const [generalSubDepartment, setGeneralSubDepartment] = React.useState("");
  const [generalCategory, setGeneralCategory] = React.useState("");
  const [generalAsset, setGeneralAsset] = React.useState("");

  const handleExportPdf = useReactToPrint({
    content: () => reportRef.current,
    documentTitle: `InspectFleet-${reportType.toLowerCase()}-compliance`,
  });

  const dateRange = getDateRange(period, customStart, customEnd);
  const periodLabel =
    PERIODS.find((p) => p.value === period)?.label || "Custom";
  const isValidDateRange =
    period !== "CUSTOM" ||
    (customStart && customEnd && customStart <= customEnd);

  const departmentQuery = useQuery({
    queryKey: ["compliance-departments"],
    queryFn: () => DepartmentServices.allDepartments(),
  });

  const subDepartmentQuery = useQuery({
    queryKey: ["compliance-subdepartments", prestartDepartment],
    queryFn: () => {
      if (!prestartDepartment) return [];
      return DepartmentServices.subDepartmentByDepartment({
        id: prestartDepartment,
      });
    },
    enabled: !!prestartDepartment,
  });

  const categoryQuery = useQuery({
    queryKey: ["compliance-asset-categories"],
    queryFn: () => AssetService.allAssetLabel("category"),
  });

  // Assets are a bounded, slow-changing list — kept separate from the
  // period-scoped compliance report so filter dropdowns stay cheap.
  const assetsQuery = useQuery({
    queryKey: ["compliance-assets"],
    queryFn: () => AssetService.allAssets(),
  });

  const complianceQuery = useQuery({
    queryKey: [
      "compliance",
      reportType,
      period,
      customStart,
      customEnd,
      prestartDepartment,
      prestartSubDepartment,
      prestartCategory,
      generalDepartment,
      generalSubDepartment,
      generalCategory,
      generalAsset,
      assetDepartment,
      assetFilter,
    ],
    queryFn: () =>
      DashboardServices.getDashboardCompliance({
        reportType,
        period,
        customStart,
        customEnd,
        department:
          reportType === "PRESTART"
            ? prestartDepartment
            : reportType === "GENERAL"
              ? generalDepartment
              : undefined,
        subDepartment:
          reportType === "PRESTART"
            ? prestartSubDepartment
            : reportType === "GENERAL"
              ? generalSubDepartment
              : undefined,
        category:
          reportType === "PRESTART"
            ? prestartCategory
            : reportType === "GENERAL"
              ? generalCategory
              : undefined,
        assetDepartment: reportType === "ASSET" ? assetDepartment : undefined,
        asset: reportType === "ASSET" ? assetFilter : undefined,
      }),
    enabled: isValidDateRange,
    // Keep showing the previous report while a new one loads instead of
    // flashing back to the full-page skeleton on every filter change.
    placeholderData: keepPreviousData,
  });

  const departments = departmentQuery.data || [];
  const subDepartments = subDepartmentQuery.data || [];
  const assetCategories = categoryQuery.data || [];
  const baseAssets = assetsQuery.data || [];
  const report = complianceQuery.data?.report;

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set("report", reportType);
    params.set("period", period);

    const optionalParams = {
      start: customStart,
      end: customEnd,
      department:
        reportType === "PRESTART"
          ? prestartDepartment
          : reportType === "GENERAL"
            ? generalDepartment
            : "",
      subDepartment:
        reportType === "PRESTART"
          ? prestartSubDepartment
          : reportType === "GENERAL"
            ? generalSubDepartment
            : "",
      category:
        reportType === "PRESTART"
          ? prestartCategory
          : reportType === "GENERAL"
            ? generalCategory
            : "",
      assetDepartment: reportType === "ASSET" ? assetDepartment : "",
      asset: reportType === "ASSET" && assetFilter !== "ALL" ? assetFilter : "",
    };

    Object.entries(optionalParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });

    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", nextUrl);
  }, [
    reportType,
    period,
    customStart,
    customEnd,
    prestartDepartment,
    prestartSubDepartment,
    prestartCategory,
    generalDepartment,
    generalSubDepartment,
    generalCategory,
    generalAsset,
    assetDepartment,
    assetFilter,
  ]);

  const shareReport = async () => {
    const reportUrl = window.location.href;

    try {
      await navigator.clipboard.writeText(reportUrl);
      setShareMessage("Report link copied to clipboard.");
    } catch (error) {
      setShareMessage(
        "Unable to copy the report link. Copy it from the browser address bar.",
      );
    }
  };

  return (
    <Box
      ref={reportRef}
      sx={{
        minHeight: "100vh",
        "@media print": {
          backgroundColor: "#FFFFFF",
          p: 0,
        },
        "& .no-print": {
          "@media print": { display: "none" },
        },
      }}
    >
      <ReportHeader
        reportType={reportType}
        period={dateRange}
        periodLabel={periodLabel}
        onShare={shareReport}
        onExportPdf={handleExportPdf}
      />

      {/* Report Type Tabs – fully responsive */}
      <Box
        className="no-print"
        mb={3}
        sx={{
          borderBottom: `1px solid ${COLORS.border}`,
          mx: { xs: -1.5, sm: 0 },
          px: { xs: 1.5, sm: 0 },
        }}
      >
        <Tabs
          value={reportType}
          onChange={(e, value) => setReportType(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 44,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.875rem", sm: "0.9rem" },
              minHeight: 44,
              px: { xs: 1.5, sm: 2.5 },
            },
            "& .Mui-selected": {
              color: `${COLORS.primary} !important`,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: COLORS.primary,
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab label="General" value="GENERAL" />
          <Tab label="Pre-start" value="PRESTART" />
          <Tab label="Asset" value="ASSET" />
        </Tabs>
      </Box>

      {/* Filters – responsive stack */}
      <Stack
        className="no-print"
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        mb={3}
        alignItems={{ xs: "stretch", sm: "flex-end" }}
        flexWrap="wrap"
        useFlexGap
      >
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 160 },
            flex: { sm: "0 0 auto" },
          }}
        >
          <InputLabel>Period</InputLabel>
          <Select
            value={period}
            label="Period"
            onChange={(e) => setPeriod(e.target.value)}
            sx={{
              borderRadius: 1.5,
              backgroundColor: COLORS.surface,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.border,
              },
            }}
          >
            {PERIODS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {reportType === "GENERAL" && (
          <>
            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 160 } }}
            >
              <InputLabel>Department</InputLabel>
              <Select
                value={generalDepartment}
                label="Department"
                onChange={(e) => {
                  setGeneralDepartment(e.target.value);
                  setGeneralSubDepartment("");
                }}
                sx={{
                  borderRadius: 1.5,
                  backgroundColor: COLORS.surface,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLORS.border,
                  },
                }}
              >
                <MenuItem value="">All departments</MenuItem>
                {departments.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {generalDepartment && (
              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: 160 } }}
              >
                <InputLabel>Sub-department</InputLabel>
                <Select
                  value={generalSubDepartment}
                  label="Sub-department"
                  onChange={(e) => setGeneralSubDepartment(e.target.value)}
                  sx={{
                    borderRadius: 1.5,
                    backgroundColor: COLORS.surface,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.border,
                    },
                  }}
                >
                  <MenuItem value="">All sub-departments</MenuItem>
                  {subDepartments.map((subDepartment) => (
                    <MenuItem key={subDepartment.id} value={subDepartment.id}>
                      {subDepartment.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 160 } }}
            >
              <InputLabel>Asset category</InputLabel>
              <Select
                value={generalCategory}
                label="Asset category"
                onChange={(e) => setGeneralCategory(e.target.value)}
                sx={{
                  borderRadius: 1.5,
                  backgroundColor: COLORS.surface,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLORS.border,
                  },
                }}
              >
                <MenuItem value="">All categories</MenuItem>
                {assetCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 160 } }}
            >
              <InputLabel>Asset</InputLabel>
              <Select
                value={generalAsset}
                label="Asset"
                onChange={(e) => setGeneralAsset(e.target.value)}
                sx={{
                  borderRadius: 1.5,
                  backgroundColor: COLORS.surface,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLORS.border,
                  },
                }}
              >
                <MenuItem value="">All assets</MenuItem>
                {baseAssets.map((asset) => (
                  <MenuItem key={asset.id} value={asset.id}>
                    {asset.assetId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {reportType === "PRESTART" && (
          <>
            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 160 } }}
            >
              <InputLabel>Department</InputLabel>
              <Select
                value={prestartDepartment}
                label="Department"
                onChange={(e) => {
                  setPrestartDepartment(e.target.value);
                  setPrestartSubDepartment("");
                }}
                sx={{
                  borderRadius: 1.5,
                  backgroundColor: COLORS.surface,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLORS.border,
                  },
                }}
              >
                <MenuItem value="">All departments</MenuItem>
                {departments.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {prestartDepartment && (
              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: 160 } }}
              >
                <InputLabel>Sub-department</InputLabel>
                <Select
                  value={prestartSubDepartment}
                  label="Sub-department"
                  onChange={(e) => setPrestartSubDepartment(e.target.value)}
                  sx={{
                    borderRadius: 1.5,
                    backgroundColor: COLORS.surface,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.border,
                    },
                  }}
                >
                  <MenuItem value="">All sub-departments</MenuItem>
                  {subDepartments.map((subDepartment) => (
                    <MenuItem key={subDepartment.id} value={subDepartment.id}>
                      {subDepartment.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 160 } }}
            >
              <InputLabel>Asset category</InputLabel>
              <Select
                value={prestartCategory}
                label="Asset category"
                onChange={(e) => setPrestartCategory(e.target.value)}
                sx={{
                  borderRadius: 1.5,
                  backgroundColor: COLORS.surface,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLORS.border,
                  },
                }}
              >
                <MenuItem value="">All categories</MenuItem>
                {assetCategories.map((category) => (
                  <MenuItem key={category.id} value={category.name}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {reportType === "ASSET" && (
          <>
            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 160 } }}
            >
              <InputLabel>Department</InputLabel>
              <Select
                value={assetDepartment}
                label="Department"
                onChange={(e) => setAssetDepartment(e.target.value)}
                sx={{
                  borderRadius: 1.5,
                  backgroundColor: COLORS.surface,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLORS.border,
                  },
                }}
              >
                <MenuItem value="">All departments</MenuItem>
                {departments.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 180 } }}
            >
              <InputLabel>Asset</InputLabel>
              <Select
                value={assetFilter}
                label="Asset"
                onChange={(e) => setAssetFilter(e.target.value)}
                sx={{
                  borderRadius: 1.5,
                  backgroundColor: COLORS.surface,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLORS.border,
                  },
                }}
              >
                <MenuItem value="ALL">All assets</MenuItem>
                {baseAssets.map((asset) => (
                  <MenuItem key={asset.assetId} value={asset.assetId}>
                    {asset.assetId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {period === "CUSTOM" && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <TextField
              type="date"
              label="Start date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
              sx={{
                minWidth: { sm: 150 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  backgroundColor: COLORS.surface,
                },
              }}
            />
            <TextField
              type="date"
              label="End date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
              error={!isValidDateRange}
              helperText={
                !isValidDateRange ? "End date must be after start date" : ""
              }
              sx={{
                minWidth: { sm: 150 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  backgroundColor: COLORS.surface,
                },
              }}
            />
          </Stack>
        )}

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => complianceQuery.refetch()}
          disabled={complianceQuery.isFetching}
          sx={{
            borderRadius: 1.5,
            borderColor: COLORS.border,
            color: COLORS.textPrimary,
            fontWeight: 600,
            backgroundColor: COLORS.surface,
            px: 2,
            height: 40,
            "&:hover": {
              borderColor: COLORS.primaryLight,
              backgroundColor: alpha(COLORS.primary, 0.04),
            },
          }}
        >
          Refresh
        </Button>
      </Stack>

      {/* Report Content */}
      {!isValidDateRange && (
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
          Please select a valid date range.
        </Alert>
      )}

      {isValidDateRange && reportType === "GENERAL" && (
        <GeneralReport
          report={report}
          isLoading={complianceQuery.isLoading}
          isFetching={complianceQuery.isFetching}
        />
      )}

      {isValidDateRange && reportType === "PRESTART" && (
        <PrestartReport
          report={report}
          isLoading={complianceQuery.isLoading}
          isFetching={complianceQuery.isFetching}
        />
      )}

      {isValidDateRange && reportType === "ASSET" && (
        <AssetReport
          report={report}
          isLoading={complianceQuery.isLoading}
          isFetching={complianceQuery.isFetching}
        />
      )}

      <Snackbar
        open={!!shareMessage}
        autoHideDuration={4000}
        onClose={() => setShareMessage("")}
        message={shareMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};
